import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ChevronLeft, Users, Loader2, CheckCircle2, AlertCircle, Search,
  ArrowRight, ShieldAlert,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

/**
 * AdminUserMergeTool — diagnoses + heals "creator dashboard shows me
 * starting over" bugs caused by case-sensitive email lookups silently
 * splitting one human into multiple user docs.
 *
 * Flow:
 *   1. Search by email (case-insensitive)
 *   2. If 2+ users come back → pick which user_id is canonical (KEEP)
 *      and which is the duplicate (DROP)
 *   3. Dry-run merge → review linked rows that would be relinked
 *   4. Commit merge → orders/sessions/referrals get transferred to KEEP
 *
 * The dropped user is archived (not deleted) — `merged_into_user_id`
 * points back so audit history is preserved.
 */
export default function AdminUserMergeTool() {
  const [email, setEmail] = useState('');
  const [searching, setSearching] = useState(false);
  const [users, setUsers] = useState([]);
  const [keepId, setKeepId] = useState('');
  const [dropId, setDropId] = useState('');
  const [dryRunResult, setDryRunResult] = useState(null);
  const [mergeResult, setMergeResult] = useState(null);
  const [merging, setMerging] = useState(false);
  const [error, setError] = useState('');
  const [allDupes, setAllDupes] = useState(null);
  const [loadingDupes, setLoadingDupes] = useState(false);

  const loadAllDupes = async () => {
    setLoadingDupes(true);
    setError('');
    try {
      const res = await axios.get(
        `${API}/api/admin/duplicate-accounts`,
        { headers: auth() },
      );
      setAllDupes(res.data);
      if ((res.data.group_count || 0) === 0) {
        toast.success('✅ Zero duplicate accounts on the platform');
      } else {
        toast.warning(`${res.data.group_count} duplicate groups need merging`);
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to load duplicates');
      toast.error('Failed to load duplicates');
    } finally {
      setLoadingDupes(false);
    }
  };

  const pickGroup = (group) => {
    // Auto-populate the merge picker with this group's email so the
    // admin can act on it in one click.
    setEmail(group.email_normalized);
    setTimeout(() => {
      const btn = document.querySelector('[data-testid="merge-search-btn"]');
      if (btn) btn.click();
    }, 100);
  };

  const search = async () => {
    if (!email.trim()) {
      toast.error('Enter an email');
      return;
    }
    setSearching(true);
    setError('');
    setUsers([]);
    setKeepId('');
    setDropId('');
    setDryRunResult(null);
    setMergeResult(null);
    try {
      const res = await axios.get(
        `${API}/api/admin/users-by-email?email=${encodeURIComponent(email.trim())}`,
        { headers: auth() },
      );
      const list = res.data?.users || [];
      setUsers(list);
      if (list.length === 0) {
        toast.info('No users found for that email');
      } else if (list.length === 1) {
        toast.success('1 user found — no merge needed');
      } else {
        toast.warning(`${list.length} duplicate accounts found — pick which to keep`);
        // Default: oldest (first by created_at asc) is the safer keep
        const sorted = [...list].sort((a, b) => (a.created_at || '').localeCompare(b.created_at || ''));
        setKeepId(sorted[0].user_id);
        setDropId(sorted[1]?.user_id || '');
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Search failed');
      toast.error('Search failed');
    } finally {
      setSearching(false);
    }
  };

  const doMerge = async (dryRun) => {
    if (!keepId || !dropId) {
      toast.error('Select both KEEP and DROP user IDs');
      return;
    }
    if (keepId === dropId) {
      toast.error('KEEP and DROP cannot be the same user');
      return;
    }
    setMerging(true);
    setError('');
    try {
      const res = await axios.post(
        `${API}/api/admin/users-merge`,
        { keep_user_id: keepId, drop_user_id: dropId, dry_run: dryRun },
        { headers: auth() },
      );
      if (dryRun) {
        setDryRunResult(res.data);
        toast.success('Dry-run complete — review then commit');
      } else {
        setMergeResult(res.data);
        toast.success('Merge committed');
        // Refresh the user list
        await search();
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Merge failed');
      toast.error('Merge failed');
    } finally {
      setMerging(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white" data-testid="admin-user-merge-tool">
      <header className="border-b border-white/10 sticky top-0 bg-black/95 backdrop-blur z-10">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/admin" className="text-neutral-400 hover:text-white inline-flex items-center gap-1 text-sm">
              <ChevronLeft className="w-4 h-4" /> Admin
            </Link>
            <span className="text-neutral-700">|</span>
            <h1 className="font-bold text-lg inline-flex items-center gap-2">
              <Users className="w-5 h-5 text-[#00F0FF]" />
              User Merge Tool
            </h1>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8 space-y-6">
        <div className="rounded-xl border border-amber-400/20 bg-amber-400/[0.04] p-5">
          <div className="flex items-start gap-3">
            <ShieldAlert className="w-5 h-5 text-amber-300 shrink-0 mt-0.5" />
            <div>
              <h2 className="font-bold text-base mb-1 text-amber-200">Fix "dashboard shows me starting over" bugs</h2>
              <p className="text-xs text-neutral-300 leading-relaxed">
                Case-sensitive email lookups historically split one creator into multiple
                user docs. When the user logs back in, their session binds to the EMPTY
                user_id and their original creator profile/products look missing.
                Use this tool to find both accounts and merge them — orders, sessions,
                referrals, and sub-affiliate overrides all get transferred to the kept
                user. Dropped user is archived (not deleted) for audit.
              </p>
            </div>
          </div>
        </div>

        {/* Bulk duplicate scanner — one-click visibility into every duplicate group */}
        <div className="rounded-xl border border-white/10 bg-neutral-950 p-5">
          <div className="flex items-center justify-between gap-3 mb-3">
            <div>
              <h3 className="font-bold text-sm mb-0.5">Bulk duplicate scanner</h3>
              <p className="text-[11px] text-neutral-400">Scan ALL users for case-variant duplicates in one shot.</p>
            </div>
            <button
              onClick={loadAllDupes}
              disabled={loadingDupes}
              data-testid="scan-all-dupes-btn"
              className="inline-flex items-center justify-center gap-1.5 px-4 py-2 bg-white/[0.06] border border-white/15 text-white font-bold rounded-lg hover:bg-white/[0.10] disabled:opacity-50 text-xs"
            >
              {loadingDupes ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
              Scan all
            </button>
          </div>

          {allDupes && allDupes.group_count === 0 && (
            <div className="rounded-lg border border-emerald-400/20 bg-emerald-400/[0.04] p-3 text-xs text-emerald-200 flex items-center gap-2" data-testid="dupes-empty">
              <CheckCircle2 className="w-4 h-4" />
              Zero duplicate accounts on the platform. The DB-level case-insensitive constraint is now blocking new ones at the source.
            </div>
          )}

          {allDupes && allDupes.group_count > 0 && (
            <div className="space-y-2" data-testid="dupes-groups">
              {allDupes.groups.map((g) => (
                <button
                  key={g.email_normalized}
                  onClick={() => pickGroup(g)}
                  data-testid={`dupe-group-${g.email_normalized}`}
                  className="w-full text-left rounded-lg border border-amber-400/20 bg-amber-400/[0.03] hover:border-amber-400/40 hover:bg-amber-400/[0.06] p-3 transition-colors"
                >
                  <div className="flex items-center justify-between gap-3 mb-1">
                    <div className="font-mono text-xs text-amber-200 font-bold truncate">{g.email_normalized}</div>
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-400/15 text-amber-200 border border-amber-400/30 uppercase tracking-wider shrink-0">
                      {g.count} accounts
                    </span>
                  </div>
                  <div className="text-[11px] text-neutral-400 truncate">
                    {g.users.map((u) => u.user_id).join(' · ')}
                  </div>
                  <div className="text-[10px] text-neutral-500 mt-0.5">Click to load → pick KEEP/DROP → merge</div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Step 1: Search */}
        <div className="rounded-xl border border-white/10 bg-neutral-950 p-5 space-y-3">
          <label className="text-[11px] uppercase tracking-wider font-bold text-neutral-400 block">
            1. Search by email (case-insensitive)
          </label>
          <div className="flex gap-2">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && search()}
              placeholder="user@example.com"
              data-testid="merge-search-input"
              className="flex-1 px-4 py-2.5 bg-black/40 border border-white/10 rounded-lg text-sm text-white placeholder:text-neutral-500 focus:border-[#00F0FF]/50 focus:outline-none font-mono"
            />
            <button
              onClick={search}
              disabled={searching}
              data-testid="merge-search-btn"
              className="inline-flex items-center justify-center gap-1.5 px-4 bg-[#00F0FF] text-black font-bold rounded-lg hover:bg-[#00F0FF]/90 disabled:opacity-50 text-sm"
            >
              {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              Search
            </button>
          </div>
        </div>

        {/* Step 2: Pick users */}
        {users.length > 0 && (
          <div className="rounded-xl border border-white/10 bg-neutral-950 p-5 space-y-3">
            <div className="flex items-center justify-between">
              <label className="text-[11px] uppercase tracking-wider font-bold text-neutral-400">
                2. {users.length} user{users.length === 1 ? '' : 's'} found
              </label>
              {users.length > 1 && (
                <span className="text-[10px] uppercase tracking-wider font-bold text-amber-300">
                  DUPLICATES DETECTED
                </span>
              )}
            </div>
            <div className="space-y-2">
              {(() => {
                // Compute the oldest user_id once so we can badge it visually.
                const sorted = [...users].sort((a, b) => (a.created_at || '').localeCompare(b.created_at || ''));
                const oldestId = sorted[0]?.user_id;
                const newestId = sorted[sorted.length - 1]?.user_id;
                // "Richer" user = the one with creator + most products + orders.
                // Usually the right KEEP target if Edward built setup on the
                // newer account by accident, but oldest is the conservative default.
                const richest = [...users].sort((a, b) => {
                  const score = (u) => (u.creator ? 100 : 0) + (u.product_count || 0) * 5 + (u.order_count || 0);
                  return score(b) - score(a);
                })[0]?.user_id;
                return users.map((u) => {
                  const isOldest = u.user_id === oldestId && users.length > 1;
                  const isNewest = u.user_id === newestId && users.length > 1 && oldestId !== newestId;
                  const isRichest = u.user_id === richest && users.length > 1;
                  return (
                <div
                  key={u.user_id}
                  data-testid={`merge-user-row-${u.user_id}`}
                  className={`rounded-lg border p-3 transition-colors ${
                    u.user_id === keepId ? 'border-emerald-400/40 bg-emerald-400/[0.04]' :
                    u.user_id === dropId ? 'border-red-400/40 bg-red-400/[0.04]' :
                    'border-white/10 bg-white/[0.02]'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5 flex-wrap mb-1">
                        <div className="font-mono text-xs text-neutral-300 truncate">{u.user_id}</div>
                        {isOldest && (
                          <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-emerald-400/15 text-emerald-300 border border-emerald-400/30 uppercase tracking-wider">
                            Oldest
                          </span>
                        )}
                        {isNewest && (
                          <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-neutral-700/40 text-neutral-300 border border-neutral-600 uppercase tracking-wider">
                            Newest
                          </span>
                        )}
                        {isRichest && !isOldest && (
                          <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-amber-400/15 text-amber-300 border border-amber-400/30 uppercase tracking-wider">
                            Most data
                          </span>
                        )}
                      </div>
                      <div className="text-sm font-semibold text-white mb-0.5">{u.name || '—'}</div>
                      <div className="text-xs text-neutral-400 truncate">{u.email}</div>
                      <div className="flex gap-3 mt-1.5 text-[11px] text-neutral-500 flex-wrap">
                        <span>
                          signed up:{' '}
                          <span className="text-white font-medium font-mono">
                            {u.created_at ? new Date(u.created_at).toLocaleString(undefined, {
                              year: 'numeric', month: 'short', day: 'numeric',
                              hour: '2-digit', minute: '2-digit',
                            }) : '—'}
                          </span>
                        </span>
                        <span>role: <span className="text-white font-medium">{u.role || '—'}</span></span>
                        <span>products: <span className="text-white font-medium">{u.product_count}</span></span>
                        <span>orders: <span className="text-white font-medium">{u.order_count}</span></span>
                        {u.creator && <span className="text-emerald-300">has creator profile</span>}
                        {u.google_id && <span className="text-blue-300">google</span>}
                      </div>
                    </div>
                    {users.length > 1 && (
                      <div className="flex flex-col gap-1 shrink-0">
                        <button
                          onClick={() => setKeepId(u.user_id)}
                          data-testid={`merge-keep-${u.user_id}`}
                          className={`text-[10px] px-2 py-1 rounded font-bold ${
                            u.user_id === keepId
                              ? 'bg-emerald-400 text-black'
                              : 'border border-emerald-400/30 text-emerald-300 hover:bg-emerald-400/[0.08]'
                          }`}
                        >
                          KEEP
                        </button>
                        <button
                          onClick={() => setDropId(u.user_id)}
                          data-testid={`merge-drop-${u.user_id}`}
                          className={`text-[10px] px-2 py-1 rounded font-bold ${
                            u.user_id === dropId
                              ? 'bg-red-400 text-black'
                              : 'border border-red-400/30 text-red-300 hover:bg-red-400/[0.08]'
                          }`}
                        >
                          DROP
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                  );
                });
              })()}
            </div>

            {users.length > 1 && (
              <div className="rounded-lg border border-blue-400/20 bg-blue-400/[0.04] p-3 text-[12px] text-blue-100 leading-snug">
                <strong className="text-blue-200">How to choose KEEP vs DROP:</strong>{' '}
                Default is the <span className="text-emerald-300 font-semibold">OLDEST</span> account
                (their first real signup). Use <span className="text-amber-300 font-semibold">MOST DATA</span> badge
                as a tiebreaker if their setup lives on a later account. The row with
                "has creator profile" + products/orders is almost always the right KEEP.
              </div>
            )}

            {users.length > 1 && keepId && dropId && (
              <div className="flex gap-2 pt-2 border-t border-white/10">
                <button
                  onClick={() => doMerge(true)}
                  disabled={merging}
                  data-testid="merge-dryrun-btn"
                  className="flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 border border-amber-400/30 bg-amber-400/[0.04] text-amber-200 font-bold rounded-lg hover:bg-amber-400/[0.08] disabled:opacity-50 text-sm"
                >
                  {merging ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  Dry-run merge (safe)
                </button>
                <button
                  onClick={() => {
                    if (window.confirm(`Merge ${dropId} → ${keepId}? This cannot be undone via the UI.`)) {
                      doMerge(false);
                    }
                  }}
                  disabled={merging || !dryRunResult}
                  data-testid="merge-commit-btn"
                  className="flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 bg-red-500 text-white font-bold rounded-lg hover:bg-red-600 disabled:opacity-30 disabled:cursor-not-allowed text-sm"
                >
                  <CheckCircle2 className="w-4 h-4" />
                  Commit merge
                </button>
              </div>
            )}
          </div>
        )}

        {/* Dry-run result */}
        {dryRunResult && (
          <div className="rounded-xl border border-amber-400/30 bg-amber-400/[0.04] p-5" data-testid="dryrun-result">
            <div className="flex items-center gap-2 mb-3 text-amber-200 font-bold text-sm">
              <AlertCircle className="w-4 h-4" />
              Dry-run preview — review before committing
            </div>
            <div className="flex items-center gap-3 mb-3 text-xs">
              <div className="rounded bg-black/40 border border-emerald-400/30 px-2 py-1 font-mono text-emerald-200">
                {dryRunResult.keep?.user_id}
              </div>
              <ArrowRight className="w-3.5 h-3.5 text-neutral-500" />
              <div className="rounded bg-black/40 border border-red-400/30 px-2 py-1 font-mono text-red-200 line-through">
                {dryRunResult.drop?.user_id}
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
              {Object.entries(dryRunResult.linked_rows_on_drop || {}).map(([k, v]) => (
                <div key={k} className="rounded bg-black/30 border border-white/10 p-2">
                  <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-bold mb-0.5">
                    {k.replace(/_/g, ' ')}
                  </div>
                  <div className="text-white font-mono">{v}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Commit result */}
        {mergeResult && (
          <div className="rounded-xl border border-emerald-400/30 bg-emerald-400/[0.04] p-5" data-testid="merge-result">
            <div className="flex items-center gap-2 mb-3 text-emerald-300 font-bold text-sm">
              <CheckCircle2 className="w-4 h-4" />
              Merge committed
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
              {Object.entries(mergeResult.actions || {}).map(([k, v]) => (
                <div key={k} className="rounded bg-black/30 border border-white/10 p-2">
                  <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-bold mb-0.5">
                    {k.replace(/_/g, ' ')}
                  </div>
                  <div className="text-white font-mono">{v}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {error && (
          <div className="rounded-xl border border-red-400/30 bg-red-400/[0.04] p-5" data-testid="merge-error">
            <div className="flex items-center gap-2 mb-1 text-red-300 font-bold text-sm">
              <AlertCircle className="w-4 h-4" />
              Error
            </div>
            <div className="text-xs text-red-200/80">{error}</div>
          </div>
        )}
      </main>
    </div>
  );
}
